/* eslint-disable react-hooks/refs */
import { forwardRef, useRef, useImperativeHandle, useCallback, useEffect, useId } from 'react';

import { X } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

import { cn } from '../../utils';

export interface SignaturePadRef {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: (type?: string) => string;
  /**
   * Restaura un trazo desde un PNG en base64 (el mismo formato que devuelve
   * `toDataURL`). Para restaurar al MONTAR, prefiere la prop `defaultValue` —
   * este método es para restaurarlo más tarde, p. ej. tras un fetch async que
   * resuelve después de que el pad ya está en pantalla.
   */
  fromDataURL: (dataUrl: string) => void;
}

/**
 * Puntos mínimos para considerar que hay firma.
 *
 * `signature_pad` marca el lienzo como NO vacío en cuanto arranca un trazo, así
 * que un clic suelto —que dibuja un punto de un par de píxeles— bastaba para
 * dar el campo por firmado. En el propietario real eso habilitaba CONTINUAR sin
 * firma real. Un garabato de verdad deja cientos de puntos; el umbral sólo
 * descarta el toque accidental.
 */
const MIN_SIGNATURE_POINTS = 12;

/**
 * Puntos de un grupo de trazo, tolerando las dos formas de `toData()`: la 2.x
 * de `signature_pad` —la instalada— devuelve un ARRAY de arrays de puntos; la
 * 4.x, objetos `{ points }`. Leer sólo la segunda daba cero siempre, y con eso
 * ninguna firma contaba.
 */
function countPoints(group: unknown): number {
  if (Array.isArray(group)) return group.length;
  const points = (group as { points?: unknown[] } | null)?.points;
  return Array.isArray(points) ? points.length : 0;
}

export interface SignaturePadProps {
  label?: string;
  error?: string;
  hint?: string;
  height?: number;
  penColor?: string;
  disabled?: boolean;
  onChange?: (isEmpty: boolean) => void;
  /**
   * Trazo con el que arranca el pad, mismo formato que `toDataURL`. Se dibuja
   * UNA vez al montar —no es controlado: si cambia en un re-render no vuelve a
   * dibujarse, para no pelear con lo que el usuario esté trazando—. Pensado
   * para restaurar un trazo guardado en memoria (nunca en storage: sigue
   * siendo PII), no para sincronizar el pad con estado externo en vivo.
   */
  defaultValue?: string;
  /**
   * Muestra la ⓧ de limpiar sobre el lienzo. Default `true`. Ponla en `false`
   * cuando la pantalla ya ofrezca su propio CTA de limpiar (p. ej. un botón
   * "Limpiar firma" en el encabezado): dos controles para lo mismo confunden y
   * el de fuera es el que el usuario ve primero.
   */
  showClearButton?: boolean;
  className?: string;
}

export const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  (
    {
      label,
      error,
      hint,
      height = 200,
      penColor = '#2C454A',
      disabled,
      onChange,
      defaultValue,
      showClearButton = true,
      className,
    },
    ref
  ) => {
    const canvasRef = useRef<SignatureCanvas>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // Always holds the ORIGINAL signature — never overwritten during resize
    const originalSignatureRef = useRef<{ data: string; width: number; height: number } | null>(
      null
    );
    const reactId = useId();
    const componentId = `signature-pad-${reactId}`;
    const isRestoringRef = useRef(false);
    // Un trazo restaurado desde un PNG no deja puntos en `toData()` —el pad
    // dibuja la imagen, no la re-traza—, así que el conteo no lo ve. Se recuerda
    // aparte para no dar por vacía una firma que sí existe.
    const hasRestoredInkRef = useRef(false);
    const hasError = Boolean(error);

    /**
     * ¿Hay firma? No basta con que el lienzo tenga tinta: ver
     * `MIN_SIGNATURE_POINTS`. Es la ÚNICA definición de "vacío" del componente
     * —la usan `isEmpty()`, el `onChange` y por tanto el gate de los
     * formularios—, para que no puedan discrepar entre sí.
     */
    const hasSignature = useCallback(() => {
      if (hasRestoredInkRef.current) return true;
      const groups = canvasRef.current?.toData() ?? [];
      const points = (groups as unknown[]).reduce<number>(
        (total, group) => total + countPoints(group),
        0
      );
      return points >= MIN_SIGNATURE_POINTS;
    }, []);

    /**
     * Dibuja un trazo guardado y lo marca como el ORIGINAL para el resize —el
     * mismo rol que `handleEnd` le da al trazo recién dibujado por el usuario—.
     * Usa las dimensiones del buffer AL MOMENTO de restaurar: si el pad ya tiene
     * su tamaño final (caso normal, `defaultValue` al montar), coinciden con el
     * trazo guardado; si no, el próximo resize las corrige igual que con
     * cualquier otro trazo.
     *
     * `notify = false` para la restauración de `defaultValue` al montar: no es
     * una acción del usuario, es sembrar lo que ya había. Un consumer que
     * combine `onChange` con algo como "esto se editó, hay que reenviar" no
     * debe leer una restauración silenciosa como una edición.
     */
    const restoreFromDataURL = useCallback(
      (dataUrl: string, notify = true) => {
        const canvas = canvasRef.current?.getCanvas();
        if (!canvas) return;
        isRestoringRef.current = true;
        canvasRef.current?.fromDataURL(dataUrl, {
          callback: () => {
            isRestoringRef.current = false;
          },
        });
        originalSignatureRef.current = {
          data: dataUrl,
          width: canvas.width,
          height: canvas.height,
        };
        hasRestoredInkRef.current = true;
        if (notify) onChange?.(false);
      },
      [onChange]
    );

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          canvasRef.current?.clear();
          originalSignatureRef.current = null;
          hasRestoredInkRef.current = false;
          onChange?.(true);
        },
        isEmpty: () => !hasSignature(),
        toDataURL: (type?: string) => canvasRef.current?.toDataURL(type) ?? '',
        fromDataURL: (dataUrl: string) => restoreFromDataURL(dataUrl),
      }),
      [onChange, restoreFromDataURL, hasSignature]
    );

    // Restaura el trazo inicial UNA sola vez al montar. `defaultValue` no es
    // controlado a propósito —ver el prop—, así que el efecto corre con deps
    // vacías: cambiar `defaultValue` en un re-render no vuelve a dibujar nada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (defaultValue) restoreFromDataURL(defaultValue, false);
    }, []);

    // Resize handler: resize canvas buffer → restore from ORIGINAL centered
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      let debounceTimer: ReturnType<typeof setTimeout>;

      const observer = new ResizeObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const canvas = canvasRef.current?.getCanvas();
          if (!canvas) return;

          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const newBufferWidth = Math.round(container.offsetWidth * ratio);
          const newBufferHeight = Math.round(container.offsetHeight * ratio);

          // Skip if dimensions haven't changed
          if (canvas.width === newBufferWidth && canvas.height === newBufferHeight) return;

          const original = originalSignatureRef.current;

          // Update canvas buffer to match container
          canvas.width = newBufferWidth;
          canvas.height = newBufferHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Apply device pixel ratio scaling for signature_pad
          ctx.scale(ratio, ratio);

          if (!original) return;

          // Restore from ORIGINAL signature (never the resized version)
          isRestoringRef.current = true;
          const img = new Image();
          img.onload = () => {
            // Work in raw buffer pixel coordinates
            ctx.setTransform(1, 0, 0, 1, 0, 0);

            // Scale proportionally from original dimensions
            const scaleX = newBufferWidth / original.width;
            const scaleY = newBufferHeight / original.height;
            const scale = Math.min(scaleX, scaleY);
            const drawWidth = original.width * scale;
            const drawHeight = original.height * scale;
            const offsetX = (newBufferWidth - drawWidth) / 2;
            const offsetY = (newBufferHeight - drawHeight) / 2;

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

            // Restore ratio scaling for signature_pad
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

            isRestoringRef.current = false;
          };
          img.onerror = () => {
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            isRestoringRef.current = false;
          };
          img.src = original.data;
        }, 100);
      });

      observer.observe(container);
      return () => {
        observer.disconnect();
        clearTimeout(debounceTimer);
      };
    }, []);

    const handleEnd = useCallback(() => {
      if (isRestoringRef.current) return;
      const canvas = canvasRef.current?.getCanvas();
      const empty = !hasSignature();
      if (!empty && canvas) {
        // Save as the new original reference point
        originalSignatureRef.current = {
          data: canvasRef.current?.toDataURL() ?? '',
          width: canvas.width,
          height: canvas.height,
        };
      }
      onChange?.(empty);
    }, [onChange, hasSignature]);

    const handleClear = useCallback(() => {
      canvasRef.current?.clear();
      originalSignatureRef.current = null;
      hasRestoredInkRef.current = false;
      onChange?.(true);
    }, [onChange]);

    return (
      <div className={cn('w-full', className)}>
        {label && (
          <label
            htmlFor={componentId}
            className={cn(
              'mb-1.5 block text-sm font-semibold',
              hasError ? 'text-error-600' : 'text-tkf-fg',
              disabled && 'text-tkf-fg-subtle'
            )}
          >
            {label}
          </label>
        )}

        <div
          ref={containerRef}
          id={componentId}
          className={cn(
            'relative overflow-hidden rounded-xl border-2 border-dashed bg-tkf-surface-subtle',
            hasError ? 'border-error-300' : 'border-tkf-line-medium',
            disabled && 'opacity-50'
          )}
          style={{ height }}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${componentId}-error` : hint ? `${componentId}-hint` : undefined
          }
        >
          <SignatureCanvas
            ref={canvasRef}
            penColor={penColor}
            clearOnResize={false}
            canvasProps={{
              className: 'w-full h-full',
              style: { width: '100%', height: '100%' },
            }}
            onEnd={handleEnd}
          />

          {disabled && <div className="absolute inset-0 cursor-not-allowed" />}

          {!disabled && showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-2 rounded-full bg-tkf-overlay-light p-1.5 text-tkf-fg-subtle shadow-sm transition-colors hover:bg-tkf-surface hover:text-tkf-fg-muted"
              tabIndex={-1}
              aria-label="Limpiar firma"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {error && (
          <p id={`${componentId}-error`} className="mt-1 text-sm text-error-600">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${componentId}-hint`} className="mt-1 text-sm text-tkf-fg-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

SignaturePad.displayName = 'SignaturePad';
